/**
 * ClaimDetailPage (CC-Claims) — the split-out drill surface, on the
 * VendorDetailPage / SalesOrderDetailPage trio pattern.
 *
 * Header card (side + status Tag, FSM Advance affordance) + 248px section
 * Sider driving route-derived `?section=` content (overview / linked docs /
 * audit) — the selection is URL-derived, never local-only (house tenet).
 *
 * The Linked documents section is the drill: the required soLineId/poLineId
 * link reaches the SO or PO line (銷售索賠 / 採購索賠 dual placement) and, when
 * the OCC board is enabled, the 訂單控管中心 › 訂單異常 lens — where the
 * short-shipped line surfaces (OCC seed line occ-2 names this very claim). The
 * reverse rail (an OCC-side chip back to the claim) lives in the OCC module,
 * which this slice does not own — recorded as a follow-up.
 */

import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  App,
  Breadcrumb,
  Button,
  Card,
  Descriptions,
  Grid,
  Layout,
  Menu,
  Spin,
  Tag,
  Timeline,
  Typography,
  theme,
} from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  AuditOutlined,
  LinkOutlined,
  ProfileOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ListErrorState } from '@/components/common/TableContainer';
import LinkedDocumentRail from '@/components/common/LinkedDocumentRail';
import type { LinkedDocumentRailItem } from '@/components/common/LinkedDocumentRail';
import { TimestampPair } from '@/components/common/TimestampDisplay';
import { useFeature } from '@/features/flags';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { formatAbsoluteTime } from '@/utils/dateUtils';
import { CLAIM_STATUS_TRANSITIONS } from '@/types/api/claim';
import type { TransitionClaimRequest } from '@/types/api/claim';
import { useClaim, useClaimMutations } from './useClaims';
import { extractClaimApiError, CLAIM_SIDE_TAG_COLOR, CLAIM_STATUS_TAG_COLOR } from './claimConstants';
import AdvanceClaimModal from './AdvanceClaimModal';
import PageContainer from '@/components/common/PageContainer';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;

const SECTION_KEYS = ['overview', 'links', 'audit'] as const;
type ClaimDetailSection = (typeof SECTION_KEYS)[number];

const SECTION_ICONS: Record<ClaimDetailSection, React.ReactNode> = {
  overview: <ProfileOutlined />,
  links: <LinkOutlined />,
  audit: <AuditOutlined />,
};

const ClaimDetailPage = () => {
  const { t } = useTranslation(['claims', 'common']);
  const { claimId } = useParams<{ claimId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const elevation = useElevation();
  const screens = Grid.useBreakpoint();
  const occEnabled = useFeature('occ.board');

  const [searchParams, setSearchParams] = useSearchParams();
  const sectionParam = searchParams.get('section');
  const activeSection: ClaimDetailSection = SECTION_KEYS.includes(
    sectionParam as ClaimDetailSection
  )
    ? (sectionParam as ClaimDetailSection)
    : 'overview';
  const setActiveSection = (section: ClaimDetailSection) => {
    setSearchParams(section === 'overview' ? {} : { section });
  };

  const { claim, isLoading, error, refresh } = useClaim(claimId);
  const { transitionClaim } = useClaimMutations();
  const [advanceOpen, setAdvanceOpen] = useState(false);

  const allowed = claim ? CLAIM_STATUS_TRANSITIONS[claim.status] : [];

  const handleTransition = async (payload: TransitionClaimRequest): Promise<boolean> => {
    if (!claim) return false;
    try {
      await transitionClaim(claim.id, payload);
      message.success(t('claims:messages.updated'));
      setAdvanceOpen(false);
      refresh();
      return true;
    } catch (err) {
      const { message: detail } = extractClaimApiError(err);
      message.error(detail || t('claims:messages.updateFailed'));
      return false;
    }
  };

  if (error) {
    return (
      <PageContainer>
        <Card style={{ borderRadius: radiusTokens.panel }}>
          <ListErrorState
            title={t('claims:detail.loadFailed')}
            retryLabel={t('common:errors.boundary.retry')}
            onRetry={refresh}
          />
        </Card>
      </PageContainer>
    );
  }

  const isSales = claim?.side === 'sales';

  const railItems: LinkedDocumentRailItem[] = claim
    ? [
        {
          key: 'order',
          label: isSales ? t('claims:detail.rail.salesOrder') : t('claims:detail.rail.purchaseOrder'),
          emptyText: t('claims:detail.rail.none'),
          links: [
            {
              value: claim.reference.orderNumber,
              onNavigate: () =>
                navigate(
                  isSales
                    ? `/auth/sales-orders/${claim.reference.orderId}`
                    : `/auth/purchase-orders/${claim.reference.orderId}`
                ),
            },
          ],
        },
        {
          key: 'line',
          label: t('claims:detail.rail.orderLine'),
          emptyText: t('claims:detail.rail.none'),
          // The required soLineId/poLineId link — shown, not navigable (the line
          // lives inside its order document).
          links: [{ value: claim.reference.lineId }],
        },
        // Claim → OCC drill (hide-don't-disable: only when the board is enabled).
        ...(occEnabled
          ? [
              {
                key: 'occ',
                label: t('claims:detail.rail.occ'),
                emptyText: t('claims:detail.rail.none'),
                links: [
                  {
                    value: t('claims:detail.rail.occException'),
                    onNavigate: () => navigate('/auth/occ/exceptions'),
                  },
                ],
              },
            ]
          : []),
      ]
    : [];

  const menuItems = SECTION_KEYS.map((key) => ({
    key,
    icon: SECTION_ICONS[key],
    label: t(`claims:detail.sections.${key}`),
  }));

  const renderSection = () => {
    if (!claim) return null;
    switch (activeSection) {
      case 'overview':
        return (
          <Descriptions bordered column={2} size="middle">
            <Descriptions.Item label={t('claims:fields.side')}>
              <Tag color={CLAIM_SIDE_TAG_COLOR[claim.side]} style={{ margin: 0 }}>
                {t(`claims:sides.${claim.side}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('claims:fields.type')}>
              {t(`claims:types.${claim.type}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('claims:fields.status')}>
              <Tag color={CLAIM_STATUS_TAG_COLOR[claim.status]} style={{ margin: 0 }}>
                {t(`claims:statuses.${claim.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('claims:fields.remedy')}>
              {claim.remedy
                ? t(`claims:remedies.${claim.remedy}`)
                : t('claims:detail.overview.noRemedy')}
            </Descriptions.Item>
            <Descriptions.Item label={t('claims:fields.order')}>
              {claim.reference.orderNumber}
            </Descriptions.Item>
            <Descriptions.Item label={t('claims:fields.owner')}>{claim.owner}</Descriptions.Item>
            <Descriptions.Item label={t('claims:fields.quantity')}>
              {claim.quantity ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('claims:fields.amount')}>
              {claim.amount !== undefined
                ? `${claim.currency} ${claim.amount.toLocaleString()}`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('claims:detail.overview.evidence')} span={2}>
              {claim.evidenceNote}
            </Descriptions.Item>
            <Descriptions.Item label={t('claims:detail.overview.decision')} span={2}>
              {claim.decisionReason || t('claims:detail.overview.noDecision')}
            </Descriptions.Item>
          </Descriptions>
        );
      case 'links':
        return (
          <div>
            <Title
              level={5}
              style={{
                fontSize: token.fontSize,
                fontWeight: token.fontWeightStrong,
                marginBottom: token.marginMD,
              }}
            >
              {t('claims:detail.rail.title')}
            </Title>
            <LinkedDocumentRail items={railItems} ariaLabel={t('claims:detail.rail.aria')} />
          </div>
        );
      case 'audit':
        return (
          <Timeline
            items={[...claim.auditHistory].reverse().map((entry) => ({
              key: entry.id,
              children: (
                <div className="tw-flex tw-flex-col tw-gap-0.5">
                  <Text strong>
                    {entry.fromStatus
                      ? `${t(`claims:statuses.${entry.fromStatus}`)} → `
                      : ''}
                    {t(`claims:statuses.${entry.toStatus}`)}
                  </Text>
                  <Text>{entry.reason}</Text>
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {entry.actor} · {formatAbsoluteTime(entry.at, undefined, false)}
                  </Text>
                </div>
              ),
            }))}
          />
        );
    }
  };

  return (
    <PageContainer>
      <Spin spinning={isLoading}>
        {claim && (
          <>
            {/* Header card */}
            <Card
              style={{
                ...elevation.card,
                marginBottom: token.marginLG,
              }}
            >
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                <Button
                  type="text"
                  icon={<ArrowLeftOutlined />}
                  aria-label={t('claims:actions.back')}
                  onClick={() => navigate('/auth/claims')}
                />
                <Breadcrumb
                  items={[
                    { title: t('claims:detail.breadcrumbRoot') },
                    {
                      title: (
                        <a onClick={() => navigate('/auth/claims')}>
                          {t('claims:detail.breadcrumbList')}
                        </a>
                      ),
                    },
                    { title: claim.claimNumber },
                  ]}
                />
              </div>

              <div className="tw-flex tw-items-start tw-justify-between tw-gap-4 tw-flex-wrap">
                <div className="tw-flex tw-flex-col tw-gap-2">
                  <div className="tw-flex tw-items-center tw-gap-3 tw-flex-wrap">
                    <Title level={3} style={{ margin: 0 }}>
                      {claim.claimNumber}
                    </Title>
                    <Tag color={CLAIM_SIDE_TAG_COLOR[claim.side]} style={{ margin: 0 }}>
                      {t(`claims:sides.${claim.side}`)}
                    </Tag>
                    <Tag color={CLAIM_STATUS_TAG_COLOR[claim.status]} style={{ margin: 0 }}>
                      {t(`claims:statuses.${claim.status}`)}
                    </Tag>
                    <Text type="secondary">{t(`claims:types.${claim.type}`)}</Text>
                  </div>
                  <TimestampPair
                    createdAt={claim.createdAt}
                    updatedAt={claim.updatedAt}
                    direction="horizontal"
                  />
                </div>

                {allowed.length > 0 && (
                  <Button
                    type="primary"
                    size="large"
                    icon={<ArrowRightOutlined />}
                    onClick={() => setAdvanceOpen(true)}
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                  >
                    {t('claims:actions.advance')}
                  </Button>
                )}
              </div>
            </Card>

            {/* Body card — 248px section Sider + content */}
            <Card
              style={{
                ...elevation.card,
                overflow: 'hidden',
              }}
              styles={{ body: { padding: 0 } }}
            >
              <Layout style={{ backgroundColor: 'transparent' }}>
                {screens.lg && (
                  <Sider
                    width={248}
                    theme="light"
                    style={{
                      borderRight: `1px solid ${token.colorBorderSecondary}`,
                      padding: 0,
                      backgroundColor: 'transparent',
                    }}
                  >
                    <style>
                      {`
                        .claim-detail-menu { padding: 0 12px !important; }
                        .claim-detail-menu .ant-menu-item {
                          height: ${controlHeightTokens.controlHeightXL}px !important;
                          line-height: ${controlHeightTokens.controlHeightXL}px !important;
                          margin: 4px 0 !important;
                          display: flex !important;
                          align-items: center !important;
                          border-radius: ${radiusTokens.card}px !important;
                          width: 100% !important;
                        }
                      `}
                    </style>
                    <Menu
                      mode="inline"
                      selectedKeys={[activeSection]}
                      items={menuItems}
                      onClick={({ key }) => setActiveSection(key as ClaimDetailSection)}
                      className="claim-detail-menu"
                      style={{ borderRight: 'none', backgroundColor: 'transparent' }}
                    />
                  </Sider>
                )}
                <Content style={{ padding: 24, minHeight: 360, minWidth: 0 }}>
                  {!screens.lg && (
                    <Menu
                      mode="horizontal"
                      selectedKeys={[activeSection]}
                      items={menuItems}
                      onClick={({ key }) => setActiveSection(key as ClaimDetailSection)}
                      style={{ marginBottom: token.marginLG }}
                    />
                  )}
                  {renderSection()}
                </Content>
              </Layout>
            </Card>

            <AdvanceClaimModal
              open={advanceOpen}
              claim={claim}
              onClose={() => setAdvanceOpen(false)}
              onTransition={handleTransition}
            />
          </>
        )}
      </Spin>
    </PageContainer>
  );
};

export default ClaimDetailPage;
